// ============================================================
// HOOKED — MOONLIT WHARF THEME v2  (July 19, 2026)
// "The Treatment" pass — Terror-grade material & motion.
// v1 shape harvested from Terror's Gilded Obsidian theme;
// v2 deepens every material, adds the atmosphere layer, the
// brass-rail chrome vocabulary, and the shared keyframe library
// the whole overhaul stands on. Every SD/GM KEY is preserved so
// Tier-1 files port verbatim; the VALUES are Moonlit Wharf.
//
// THE PALETTE LAW (inherited, non-negotiable):
//   SD  = the WORLD — lake, night sky, water, fish, the wild.
//   GM  = the CHROME — wharf wood, lantern gold, moon silver,
//         menus, pills, meters. UI never borrows world colors
//         and the world never borrows chrome colors.
//
// THE KEYFRAME LAW (inherited, non-negotiable):
//   All animations inject via ensureKeyframes() into
//   document.head with an id-guard. NEVER inline <style>
//   blocks in JSX — they unmount with the component and
//   animations vanish app-wide.
// ============================================================

// ── SD — the WORLD (lake · night · water · fish) ─────────────
export const SD = {
  // Night sky over the lake
  void0: "#010208",
  void1: "#030816",
  void2: "#071226",
  void3: "#0c1e3a",
  void4: "#143052",
  void5: "#1d4066",   // ✦ new — horizon lift

  // Water
  water:      "#0a2a3f",
  waterDeep:  "#051826",
  waterLight: "#1d5a75",
  ripple:     "#3f93b0",
  foam:       "#bfe8f2",
  caustic:    "#6fd3e8",   // ✦ new — moonlight refracting under the surface

  // Moon & stars
  starWhite:  "#eef3f0",
  starSilver: "#c3d2d6",
  starDim:    "#7b8f96",
  moon:       "#e8f0dd",
  moonHalo:   "#f4f7e6",   // ✦ new

  // Life in the water
  gold:       "#d4a94e",   // fish-scale shimmer
  goldBright: "#f2d268",
  goldDim:    "#4d3d1a",

  purple:      "#5b3fae",  // Phantom-water glow
  purpleLight: "#a48ff5",
  purpleDim:   "#241650",

  teal:      "#0f7d8f",    // shallows
  tealLight: "#35d3e0",
  tealDim:   "#0a2d33",

  red:    "#d64545",       // tension / danger
  redBright: "#ff6b6b",    // ✦ new — the snap
  redDim: "#6e1d1d",

  // Per-Ilk aura anchors (the four, always) — world-side glow
  ilkSpace:   "#c3d2d6",
  ilkPhantom: "#a48ff5",
  ilkDragon:  "#f2a64e",
  ilkTime:    "#35d3e0",

  fontDisplay: "'Cinzel', 'Palatino Linotype', serif",
  fontUI: "'Trebuchet MS', 'Segoe UI', sans-serif",
};

// ── GM — the CHROME (wharf · lantern · menus) ────────────────
export const GM = {
  // Wharf wood steps (page depths)
  page:      "#100b06",
  pageDeep:  "#0a0703",
  pageWarm:  "#1a120a",   // shallow wood step
  panel:     "#221709",
  panelHi:   "#2c1f0e",   // ✦ new — lit face of a plank
  panelEdge: "#3a2a14",

  // Lantern gold — the primary metal
  gold:       "#d9a83f",
  goldBright: "#f5cd5e",
  goldPale:   "#ffe9a8",   // ✦ new — the hot specular line on brass
  goldDeep:   "#a97e28",   // ✦ new — brass in shadow
  goldDim:    "#57431a",

  // Moon silver — secondary ornament
  silver:       "#b8c4c9",
  silverBright: "#e6eef0",
  silverDim:    "#4e585c",

  // Rope & net accents
  rope:    "#a3773c",
  ropeLight: "#c9974f",   // ✦ new
  ropeDim: "#5c421f",

  // Vitals
  rose:    "#e2607a",
  roseBright: "#ff87a0",  // ✦ new
  roseDim: "#6e2635",

  // Text
  ink:      "#efe6d2",
  inkDim:   "#9c8f74",
  inkBrown: "#6b5b40",   // muted borders

  fontDisplay: "'Cinzel', 'Palatino Linotype', serif",
  fontBody: "'Cormorant Garamond', Georgia, serif",
  fontUI: "'Trebuchet MS', 'Segoe UI', sans-serif",
};

// ── Material helpers (harvested shape, deepened) ─────────────
export const LANTERN_METAL = `linear-gradient(180deg, ${GM.goldPale} 0%, ${GM.goldBright} 22%, ${GM.gold} 50%, ${GM.goldDeep} 78%, ${GM.goldDim} 100%)`;
export const BRASS_RAIL   = `linear-gradient(90deg, ${GM.goldDim}, ${GM.gold} 12%, ${GM.goldPale} 50%, ${GM.gold} 88%, ${GM.goldDim})`;
export const WHARF_SLAB   = `linear-gradient(168deg, ${GM.panelHi} 0%, ${GM.panel} 34%, ${GM.pageWarm} 78%, ${GM.pageDeep} 100%)`;
export const WHARF_RECESS = `linear-gradient(180deg, ${GM.pageDeep}, ${GM.page})`;
export const NIGHT_BG     = `radial-gradient(120% 90% at 50% 8%, ${SD.void4} 0%, ${SD.void2} 34%, ${SD.void1} 62%, ${SD.void0} 100%)`;
export const DEEP_WATER   = `linear-gradient(180deg, ${SD.water} 0%, ${SD.waterDeep} 100%)`;
export const GLASS        = `linear-gradient(160deg, rgba(255,255,255,0.06), rgba(255,255,255,0.01) 40%, rgba(0,0,0,0.10))`;

// The octagon facet — the house silhouette (kept identical).
export const facet = (px = 10) =>
  `polygon(${px}px 0, calc(100% - ${px}px) 0, 100% ${px}px, 100% calc(100% - ${px}px), calc(100% - ${px}px) 100%, ${px}px 100%, 0 calc(100% - ${px}px), 0 ${px}px)`;

// Multi-layer bevel: a lit top-left, a shadowed bottom-right, an
// inner brass hairline. The single biggest "AAA panel" upgrade.
export const bevelPanel = (accent = GM.gold) => ({
  boxShadow: [
    `inset 0 1px 0 ${GM.goldPale}22`,
    `inset 1px 0 0 ${GM.goldPale}14`,
    `inset 0 -2px 0 rgba(0,0,0,0.45)`,
    `inset -2px 0 0 rgba(0,0,0,0.30)`,
    `inset 0 0 0 1px ${accent}33`,
    `0 8px 30px rgba(0,0,0,0.62)`,
    `0 2px 6px rgba(0,0,0,0.5)`,
  ].join(", "),
});

export const textGlow = (c = GM.gold) => ({ textShadow: `0 0 8px ${c}88, 0 0 22px ${c}33` });
export const textEngrave = () => ({ textShadow: `0 1px 0 rgba(0,0,0,0.6), 0 -1px 0 rgba(255,255,255,0.06)` });
export const bloom = (c = GM.gold) => `0 0 14px ${c}55, 0 0 34px ${c}22`;

// Per-Ilk aura color lookup — world-side. One source, no drift.
export const ilkAura = (ilk) => ({
  Space:   SD.ilkSpace,
  Phantom: SD.ilkPhantom,
  Dragon:  SD.ilkDragon,
  Time:    SD.ilkTime,
}[ilk] || SD.foam);

// ── ensureKeyframes — THE injection law ──────────────────────
// id-guarded, head-mounted, survives every unmount.
export function ensureKeyframes(id, css) {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}

// Core shared animations — injected once at App mount.
// Expanded library: v1 set kept verbatim, v2 additions below the line.
export function ensureCoreKeyframes() {
  ensureKeyframes("hooked-core-kf", `
    @keyframes bubbleFloat { from { transform: translateX(-50%) translateY(0); } to { transform: translateX(-50%) translateY(-6px); } }
    @keyframes bobberIdle { 0%,100% { transform: translateY(0) rotate(-2deg); } 50% { transform: translateY(3px) rotate(2deg); } }
    @keyframes bobberNibble { 0%,100% { transform: translateY(0); } 40% { transform: translateY(7px); } 60% { transform: translateY(2px); } }
    @keyframes bobberBite { 0% { transform: translateY(0); } 30% { transform: translateY(14px); } 100% { transform: translateY(10px); } }
    @keyframes rippleOut { from { transform: scale(0.4); opacity: 0.9; } to { transform: scale(2.2); opacity: 0; } }
    @keyframes moonShimmer { 0%,100% { opacity: 0.35; } 50% { opacity: 0.7; } }
    @keyframes tensionPulse { 0%,100% { opacity: 0.65; } 50% { opacity: 1; } }
    @keyframes catchGlow { from { box-shadow: 0 0 10px #d9a83f33; } to { box-shadow: 0 0 30px #f5cd5e88; } }
    @keyframes beatPop { from { transform: scale(1.35); opacity: 1; } to { transform: scale(1); opacity: 0.85; } }
    @keyframes fadeUp { from { transform: translateY(14px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

    /* ── v2 atmosphere & material ─────────────────────────── */
    @keyframes starTwinkle { 0%,100% { opacity: var(--s0,0.25); } 50% { opacity: var(--s1,0.9); } }
    @keyframes moteDrift { 0% { transform: translate(0,0); opacity:0; } 12% { opacity:0.7; } 88% { opacity:0.6; } 100% { transform: translate(var(--mx,20px), -110vh); opacity:0; } }
    @keyframes causticShift { 0% { background-position: 0 0, 0 0; } 100% { background-position: 240px 120px, -180px 90px; } }
    @keyframes waterBreathe { 0%,100% { opacity: 0.5; transform: scaleY(1); } 50% { opacity: 0.85; transform: scaleY(1.06); } }
    @keyframes moonBreathe { 0%,100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 0.9; transform: scale(1.04); } }
    @keyframes railSheen { 0% { background-position: -220% 0; } 100% { background-position: 320% 0; } }
    @keyframes vignettePulse { 0%,100% { opacity: 0.9; } 50% { opacity: 1; } }
    @keyframes titleRise { 0% { letter-spacing: 2px; opacity:0; transform: translateY(18px); filter: blur(6px);} 60%{opacity:1; filter:blur(0);} 100% { letter-spacing: 14px; opacity:1; transform: translateY(0);} }
    @keyframes emberFloat { 0% { transform: translateY(0) translateX(0); opacity:0; } 15%{opacity:0.9;} 100% { transform: translateY(-46px) translateX(var(--ex,6px)); opacity:0; } }
    @keyframes finSway { 0%,100% { transform: rotate(-3deg); } 50% { transform: rotate(3deg); } }
    @keyframes fishHover { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
    @keyframes lanternFlicker { 0%,100% { opacity:0.92; } 45%{opacity:1;} 55%{opacity:0.86;} 70%{opacity:0.98;} }
    @keyframes markerApproach { from { filter: brightness(0.7); } to { filter: brightness(1.25); } }
    @keyframes snapShake { 0%,100%{transform:translateX(-50%);} 20%{transform:translateX(calc(-50% - 6px));} 40%{transform:translateX(calc(-50% + 6px));} 60%{transform:translateX(calc(-50% - 4px));} 80%{transform:translateX(calc(-50% + 4px));} }
    @keyframes landBurst { 0%{transform:translate(-50%,-50%) scale(0.2); opacity:0.95;} 100%{transform:translate(-50%,-50%) scale(2.6); opacity:0;} }
    @keyframes sparkle { 0%{transform:scale(0) rotate(0deg); opacity:0;} 40%{transform:scale(1) rotate(90deg); opacity:1;} 100%{transform:scale(0) rotate(180deg); opacity:0;} }
    @keyframes gradeReveal { 0%{opacity:0; transform:translateY(8px) scale(0.9);} 100%{opacity:1; transform:translateY(0) scale(1);} }
    @keyframes ringPulseGlow { 0%,100%{opacity:0.4;} 50%{opacity:0.85;} }
    @keyframes prophecyWaver { 0%,100%{transform:translateY(0) scaleX(1);} 50%{transform:translateY(-3px) scaleX(1.02);} }

    /* ── moved into CORE (was HoloFoil-only → dead before a foil
          card ever mounted; the reel current + splash lean on it) ─ */
    @keyframes foilDrift { 0% { transform: translateX(-8%);} 100% { transform: translateX(8%);} }

    /* ── v3 continuous-world (the grid dies here) ─────────────── */
    @keyframes gladeShimmer { 0%,100% { opacity: 0.42; transform: translateX(-50%) scaleY(1);} 50% { opacity: 0.72; transform: translateX(-50%) scaleY(1.05);} }
    @keyframes swellDrift { 0% { transform: translateY(0); } 100% { transform: translateY(-14px); } }
    @keyframes causticCreep { 0% { background-position: 0 0, 0 0, 0 0; } 100% { background-position: 90px 60px, -70px 40px, 40px -30px; } }
    @keyframes plankBreathe { 0%,100% { opacity: 0.5; } 50% { opacity: 0.72; } }
    @keyframes pilingWake { 0%,100% { opacity: 0.3; transform: translate(-50%,0) scaleX(1);} 50% { opacity: 0.6; transform: translate(-50%,0) scaleX(1.35);} }
    @keyframes lanternPool { 0%,100% { opacity: 0.55; transform: translate(-50%,-50%) scale(1);} 50% { opacity: 0.8; transform: translate(-50%,-50%) scale(1.06);} }
    @keyframes anglerBreathe { 0%,100% { transform: translateY(0) scaleY(1);} 50% { transform: translateY(-1px) scaleY(1.012);} }
    @keyframes shoreLap { 0%,100% { opacity: 0.35; transform: scaleX(1);} 50% { opacity: 0.6; transform: scaleX(1.02);} }

    /* ── v5 THE FEEL PASS — make the hands believe it ─────────── */
    /* the cast: wind back, then whip forward */
    @keyframes castWindup { 0% { transform: rotate(0deg);} 100% { transform: rotate(-26deg);} }
    @keyframes castRelease { 0% { transform: rotate(-26deg);} 45% { transform: rotate(34deg);} 70% { transform: rotate(24deg);} 100% { transform: rotate(28deg);} }
    /* the line + lure sailing out along an arc, then dropping */
    @keyframes lureSail { 0% { transform: translate(0,0) scale(0.6); opacity: 0;} 12% { opacity: 1;} 70% { opacity: 1;} 100% { transform: translate(var(--sailX,220px), var(--sailY,40px)) scale(1); opacity: 1;} }
    @keyframes lineWhip { 0% { opacity: 0; stroke-dashoffset: 400;} 20% { opacity: 1;} 100% { opacity: 1; stroke-dashoffset: 0;} }
    /* the plunk — impact rings when the lure hits water */
    @keyframes plunkRing { 0% { transform: translate(-50%,-50%) scale(0.15); opacity: 0.95;} 100% { transform: translate(-50%,-50%) scale(2.8); opacity: 0;} }
    @keyframes plunkSplash { 0% { transform: translate(-50%,-50%) scaleY(0.2); opacity: 0.9;} 40% { transform: translate(-50%,-90%) scaleY(1); opacity: 0.9;} 100% { transform: translate(-50%,-60%) scaleY(0.4); opacity: 0;} }
    /* the bobber settling after it lands (damped bounce) */
    @keyframes bobberSettle { 0% { transform: translateY(-18px) scale(0.9);} 40% { transform: translateY(4px) scale(1.04);} 65% { transform: translateY(-2px);} 100% { transform: translateY(0) scale(1);} }
    /* the TAKE — the bite yanks the bobber under, hard */
    @keyframes bobberTake { 0% { transform: translateY(0) scale(1);} 25% { transform: translateY(20px) scale(1.05,0.92);} 55% { transform: translateY(30px) scale(0.9,1.1); opacity: 0.85;} 100% { transform: translateY(42px) scale(0.8); opacity: 0.5;} }
    @keyframes takeShadow { 0% { opacity: 0.2; transform: translate(-50%,-50%) scale(0.6);} 100% { opacity: 0.55; transform: translate(-50%,-50%) scale(1.5);} }
    /* the hookset — a sharp jolt when you set the hook */
    @keyframes hooksetJolt { 0% { transform: translate(-50%,-50%) scale(1);} 20% { transform: translate(-50%,-58%) scale(1.14);} 45% { transform: translate(-50%,-48%) scale(0.94);} 70% { transform: translate(-50%,-52%) scale(1.04);} 100% { transform: translate(-50%,-50%) scale(1);} }
    @keyframes hooksetFlash { 0% { opacity: 0; transform: translate(-50%,-50%) scale(0.4);} 30% { opacity: 0.9;} 100% { opacity: 0; transform: translate(-50%,-50%) scale(2.2);} }
    /* the fight: rod under load, straining */
    @keyframes rodStrain { 0%,100% { transform: rotate(0deg);} 50% { transform: rotate(2.5deg);} }
    @keyframes rodYank { 0% { transform: rotate(0deg);} 30% { transform: rotate(-6deg);} 100% { transform: rotate(0deg);} }
    /* the fish on the line — headshakes and thrash */
    @keyframes fishThrash { 0%,100% { transform: translateX(-50%) rotate(-6deg);} 50% { transform: translateX(-50%) rotate(6deg);} }
    @keyframes fishHeadshake { 0% { transform: translate(-50%,0) rotate(0deg);} 25% { transform: translate(-52%,0) rotate(-5deg);} 75% { transform: translate(-48%,0) rotate(5deg);} 100% { transform: translate(-50%,0) rotate(0deg);} }
    /* line tension shimmer while a fish pulls */
    @keyframes lineTaut { 0%,100% { opacity: 0.6; } 50% { opacity: 1; } }
    /* the landing: the fish breaks the surface */
    @keyframes breach { 0% { transform: translate(-50%,60px) scale(0.7) rotate(-14deg); opacity: 0;} 30% { opacity: 1;} 55% { transform: translate(-50%,-10px) scale(1.05) rotate(6deg);} 100% { transform: translate(-50%,0) scale(1) rotate(0deg); opacity: 1;} }
    @keyframes breachSpray { 0% { transform: translate(-50%,0) scale(0.3); opacity: 0;} 30% { opacity: 0.9;} 100% { transform: translate(-50%,-40px) scale(1.6); opacity: 0;} }
    /* screen kick — a tiny camera shake on hookset / snap */
    @keyframes screenKick { 0%,100% { transform: translate(0,0);} 25% { transform: translate(-2px,2px);} 50% { transform: translate(2px,-1px);} 75% { transform: translate(-1px,1px);} }

    /* ── v5 world polish ──────────────────────────────────────── */
    @keyframes deepWave { 0% { transform: translateX(-50%); opacity: 0.25;} 50% { opacity: 0.5;} 100% { transform: translateX(-46%); opacity: 0.25;} }
    @keyframes whitecap { 0%,100% { opacity: 0;} 50% { opacity: 0.5;} }
    @keyframes boatWake { 0% { transform: translate(-50%,0) scaleX(0.4); opacity: 0.5;} 100% { transform: translate(-50%,26px) scaleX(1.6); opacity: 0;} }
    @keyframes moonDiscRipple { 0%,100% { transform: translate(-50%,-50%) scaleY(1); opacity: 0.7;} 50% { transform: translate(-50%,-50%) scaleY(0.82); opacity: 0.9;} }
  `);
}
